import { Controller } from "@hotwired/stimulus"
import { rollSwap } from "../helpers/readout_roll"

// Budget card bar — Left and Upcoming.
//
// The bar is the whole budget: Spent from the start, then Left, with Upcoming
// drawn as a section inside Left. The card's end column is the readout and
// the control in one: tapping it (or a run on the bar) flips the column to
// the other figure and clamps two pins onto that run's extent. Spent is never
// a target — it is already the hero on the start side.
//
// One tap, one motion. The readout rolls (readout_roll) — the old figure
// lifts out, the new one rises into the same box — while the pins spring
// onto the run, the run flares once as they land and the arrow after the
// label turns over. Nothing around the column moves.
export default class extends Controller {
  static targets = ["bar", "pinStart", "pinEnd", "label", "value", "glyph", "item"]
  static values = {
    spans: Object,   // { left: [start, end], upcoming: [start, end] }
    labels: Object,  // { left: "Left", upcoming: "Upcoming" }
    amounts: Object, // { left: "$1,299.30", upcoming: "$423.45" }
    tones: Object,   // { left: "text-blue-500", upcoming: "text-error" }
    selected: String
  }

  // Flare + grip on the bar, matching the CSS keyframes (delay + duration).
  static FLARE_MS = 640

  // A pin sitting exactly on 100% straddles the bar's rounded cap, so the
  // closing pin is held just inside the end.
  static END_INSET_PX = 2

  static KEYS = ["left", "upcoming"]

  connect() {
    this._place(this.selectedValue, { animate: false })
  }

  // data-action="click->budget-bar#toggle" on the readout column.
  toggle(event) {
    const [a, b] = this.constructor.KEYS
    this._choose(this.selectedValue === a ? b : a, event)
  }

  // data-action="click->budget-bar#select" data-budget-bar-key-param="upcoming"
  // on the bar's hit targets.
  select(event) {
    this._choose(event.params.key, event)
  }

  _choose(key, event) {
    if (!key || key === this.selectedValue) return

    // The carousel listens for drags on the stage this card sits in; a tap on
    // the bar is ours and should not read as anything else.
    event.stopPropagation()

    this.selectedValue = key
    this._tap(event.currentTarget)
    this._place(key, { animate: true })
    this._flare()
    this._swapColumn(key)
  }

  // Haptic only for a tap that actually changes something. It used to run as
  // its own action on the element, which fired it ahead of the guard above —
  // so tapping what was already lit still buzzed.
  _tap(element) {
    const haptic = this.application.getControllerForElementAndIdentifier(element, "bridge--haptic")
    haptic?.tap()
  }

  // ── internals ─────────────────────────────────────────────────────

  _place(key, { animate }) {
    const span = this.spansValue[key]
    if (!span || !this.hasPinStartTarget) return

    const pins = [this.pinStartTarget, this.pinEndTarget]
    if (!animate) pins.forEach((pin) => { pin.style.transition = "none" })

    this.pinStartTarget.style.insetInlineStart = `${span[0]}%`
    this.pinEndTarget.style.insetInlineStart =
      span[1] >= 100 ? `calc(100% - ${this.constructor.END_INSET_PX}px)` : `${span[1]}%`

    if (!animate) {
      requestAnimationFrame(() => pins.forEach((pin) => { pin.style.transition = "" }))
    }

    if (this.hasBarTarget) this.barTarget.dataset.selected = key
    if (this.hasGlyphTarget) this.glyphTarget.classList.toggle("is-flipped", key !== this.constructor.KEYS[0])
    this.itemTargets.forEach((item) => {
      item.setAttribute("aria-pressed", String(item.dataset.budgetBarKeyParam === key))
    })
  }

  // The chosen run flares and the pins grip once they land. The class is
  // taken off again so the next tap can play it fresh; re-adding it in the
  // same frame would not restart a CSS animation.
  _flare() {
    if (!this.hasBarTarget) return
    const bar = this.barTarget
    bar.classList.remove("is-flaring")
    void bar.offsetWidth
    bar.classList.add("is-flaring")
    clearTimeout(this._flareTimer)
    this._flareTimer = setTimeout(() => bar.classList.remove("is-flaring"), this.constructor.FLARE_MS)
  }

  // Label and figure roll together, so the label never lands a frame before
  // its figure. The tone sits on the label's row, so the arrow beside it
  // takes the same colour.
  _swapColumn(key) {
    if (!this.hasLabelTarget || !this.hasValueTarget) return

    const label = this.labelTarget
    const value = this.valueTarget
    const tone = this.tonesValue[key]

    this._roll?.cancel()
    this._roll = rollSwap([label, value], () => {
      label.textContent = this.labelsValue[key]
      value.textContent = this.amountsValue[key]
      if (tone) {
        label.parentElement.classList.remove("text-blue-500", "text-error")
        label.parentElement.classList.add(tone)
      }
    })
  }

  disconnect() {
    this._roll?.cancel()
    clearTimeout(this._flareTimer)
  }
}
